import {
  collection,
  doc,
  addDoc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { createPrize, type PrizeType } from './prizes';
import { tallyDanceOffVotes, type DanceOffTieBreak } from '../utils/danceOffTally';

export const MIN_DANCE_OFF_CONTESTANTS = 2;
export const MAX_DANCE_OFF_CONTESTANTS = 12;
export const DANCE_OFF_PRIZE_TIMEOUT_MINUTES = 15;

export interface DanceOffWinner {
  contestant: number;
  prizeId: string | null;
  claimId: string | null;
}

export interface DanceOff {
  id: string;
  eventId: string;
  djId: string;
  title: string;
  contestantCount: number;
  status: 'open' | 'closed' | 'cancelled';
  prizeType: PrizeType;
  prizeCustomText: string | null;
  createdAt: string;
  closedAt: string | null;
  results: { counts: number[]; totalVotes: number } | null;
  tieBreak: DanceOffTieBreak | null;
  winners: DanceOffWinner[];
}

export interface DanceOffVote {
  danceOffId: string;
  eventId: string;
  attendeeId: string;
  contestant: number;
  votedAt: string;
}

export interface DanceOffClaim {
  id: string;
  danceOffId: string;
  eventId: string;
  contestant: number;
  prizeId: string;
  token: string;
  usedBy: string | null;
  usedAt: string | null;
}

function docToDanceOff(docSnap: DocumentSnapshot): DanceOff {
  const data = docSnap.data() ?? {};
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    title: data.title || 'Dance-off',
    contestantCount: data.contestantCount,
    status: data.status,
    prizeType: data.prizeType,
    prizeCustomText: data.prizeCustomText || null,
    createdAt: data.createdAt,
    closedAt: data.closedAt || null,
    results: data.results
      ? { counts: Array.isArray(data.results.counts) ? data.results.counts : [], totalVotes: data.results.totalVotes ?? 0 }
      : null,
    tieBreak: data.tieBreak || null,
    winners: Array.isArray(data.winners)
      ? data.winners.map((winner: Record<string, unknown>) => ({
        contestant: winner.contestant as number,
        prizeId: (winner.prizeId as string) || null,
        claimId: (winner.claimId as string) || null,
      }))
      : [],
  };
}

function docToDanceOffVote(docSnap: DocumentSnapshot): DanceOffVote {
  const data = docSnap.data() ?? {};
  return {
    danceOffId: data.danceOffId,
    eventId: data.eventId,
    attendeeId: data.attendeeId,
    contestant: data.contestant,
    votedAt: data.votedAt,
  };
}

function docToDanceOffClaim(docSnap: DocumentSnapshot): DanceOffClaim {
  const data = docSnap.data() ?? {};
  return {
    id: docSnap.id,
    danceOffId: data.danceOffId,
    eventId: data.eventId,
    contestant: data.contestant,
    prizeId: data.prizeId,
    token: data.token,
    usedBy: data.usedBy || null,
    usedAt: data.usedAt || null,
  };
}

export function danceOffVoteId(danceOffId: string, attendeeId: string): string {
  return `${danceOffId}_${attendeeId}`;
}

export function danceOffClaimId(danceOffId: string, contestant: number): string {
  return `${danceOffId}_${contestant}`;
}

function generateClaimToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// --- DJ actions ---

export async function createDanceOff(
  eventId: string,
  djId: string,
  options: { title: string; contestantCount: number; prizeType: PrizeType; prizeCustomText: string | null },
): Promise<string> {
  const docRef = await addDoc(collection(db, 'danceOffs'), {
    eventId,
    djId,
    title: options.title.trim() || 'Dance-off',
    contestantCount: options.contestantCount,
    status: 'open',
    prizeType: options.prizeType,
    prizeCustomText: options.prizeCustomText,
    createdAt: new Date().toISOString(),
    closedAt: null,
    results: null,
    tieBreak: null,
    winners: [],
  });
  return docRef.id;
}

export async function cancelDanceOff(danceOffId: string): Promise<void> {
  await updateDoc(doc(db, 'danceOffs', danceOffId), {
    status: 'cancelled',
    closedAt: new Date().toISOString(),
  });
}

export async function closeDanceOff(
  danceOff: DanceOff,
  votes: { contestant: number }[],
  winnerContestants: number[],
  tieBreak: DanceOffTieBreak,
): Promise<void> {
  const winners: DanceOffWinner[] = [];
  for (const contestant of winnerContestants) {
    const prizeId = await createPrize(danceOff.eventId, danceOff.djId, danceOff.prizeType, {
      customText: danceOff.prizeCustomText ?? undefined,
      timeoutMinutes: DANCE_OFF_PRIZE_TIMEOUT_MINUTES,
      gameSource: 'dance_off',
      gameRoundId: danceOff.id,
      tieBreakMethod: tieBreak === 'none' ? undefined : tieBreak,
      placement: contestant,
    });
    const claimId = danceOffClaimId(danceOff.id, contestant);
    await setDoc(doc(db, 'danceOffClaims', claimId), {
      danceOffId: danceOff.id,
      eventId: danceOff.eventId,
      contestant,
      prizeId,
      token: generateClaimToken(),
      usedBy: null,
      usedAt: null,
    });
    winners.push({ contestant, prizeId, claimId });
  }

  await updateDoc(doc(db, 'danceOffs', danceOff.id), {
    status: 'closed',
    closedAt: new Date().toISOString(),
    results: tallyDanceOffVotes(votes, danceOff.contestantCount),
    tieBreak,
    winners,
  });
}

// --- Attendee actions ---

export async function castDanceOffVote(
  danceOffId: string,
  eventId: string,
  attendeeId: string,
  contestant: number,
): Promise<void> {
  await setDoc(doc(db, 'danceOffVotes', danceOffVoteId(danceOffId, attendeeId)), {
    danceOffId,
    eventId,
    attendeeId,
    contestant,
    votedAt: new Date().toISOString(),
  });
}

export async function getDanceOff(danceOffId: string): Promise<DanceOff | null> {
  const snapshot = await getDoc(doc(db, 'danceOffs', danceOffId));
  return snapshot.exists() ? docToDanceOff(snapshot) : null;
}

export async function claimDanceOffPrize(input: { claimId: string; token: string; nickname?: string }): Promise<{ prizeId: string }> {
  const callable = httpsCallable<{ claimId: string; token: string; nickname?: string }, { prizeId: string }>(
    functions,
    'claimDanceOffPrize',
  );
  const { data } = await callable(input);
  return data;
}

export function danceOffClaimUrl(eventUrlOrId: string, claimId: string, token: string): string {
  return `${window.location.origin}/e/${encodeURIComponent(eventUrlOrId)}/claim/${encodeURIComponent(claimId)}?t=${token}`;
}

// --- Subscriptions ---

export function subscribeToDJDanceOffs(
  eventId: string,
  callback: (danceOffs: DanceOff[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(collection(db, 'danceOffs'), where('eventId', '==', eventId), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToDanceOff));
  }, (error) => {
    console.error('[DanceOff] History listener failed:', error);
    callback([]);
    onError?.(error);
  });
}

export function subscribeToLatestDanceOff(
  eventId: string,
  callback: (danceOff: DanceOff | null) => void,
): Unsubscribe {
  const q = query(collection(db, 'danceOffs'), where('eventId', '==', eventId), orderBy('createdAt', 'desc'), limit(1));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.empty ? null : docToDanceOff(snapshot.docs[0]));
  }, (error) => {
    console.error('[DanceOff] Latest dance-off listener failed:', error);
    callback(null);
  });
}

export function subscribeToOpenDanceOff(
  eventId: string,
  callback: (danceOff: DanceOff | null) => void,
): Unsubscribe {
  return subscribeToLatestDanceOff(eventId, (latest) => {
    callback(latest?.status === 'open' ? latest : null);
  });
}

export function subscribeToDanceOffVotes(
  eventId: string,
  danceOffId: string,
  callback: (votes: DanceOffVote[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'danceOffVotes'),
    where('eventId', '==', eventId),
    where('danceOffId', '==', danceOffId),
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToDanceOffVote));
  }, (error) => {
    console.error('[DanceOff] Votes listener failed:', error);
    callback([]);
    onError?.(error);
  });
}

export function subscribeToDanceOffClaim(
  claimId: string,
  callback: (claim: DanceOffClaim | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(doc(db, 'danceOffClaims', claimId), (snapshot) => {
    callback(snapshot.exists() ? docToDanceOffClaim(snapshot) : null);
  }, (error) => {
    console.error('[DanceOff] Claim listener failed:', error);
    callback(null);
    onError?.(error);
  });
}

export function subscribeToMyDanceOffVote(
  danceOffId: string,
  attendeeId: string,
  callback: (vote: DanceOffVote | null) => void,
): Unsubscribe {
  return onSnapshot(doc(db, 'danceOffVotes', danceOffVoteId(danceOffId, attendeeId)), (snapshot) => {
    callback(snapshot.exists() ? docToDanceOffVote(snapshot) : null);
  }, (error) => {
    console.error('[DanceOff] My vote listener failed:', error);
    callback(null);
  });
}
